import { useGetGuidParam } from '@risksmart-app/components/src/routes/routes.utils';
import type { FC } from 'react';
import { useNavigate } from 'react-router';
import { useI18NSummaryHelpContent } from 'src/components/help-panel/useSummaryHelpContent';
import { useGetEnterpriseRiskById } from 'src/hooks/queries/enterprise-risk/useGetEnterpriseRiskById';
import RiskForm from 'src/pages/risks/forms/RiskForm';
import type { RiskFormDataFields } from 'src/pages/risks/forms/riskSchema';
import { defaultValues } from 'src/pages/risks/forms/riskSchema';
import { useHasPermissionQuery } from 'src/rbac/useHasPermission';

import { useUpdateEnterpriseRisk } from '@/hooks/mutations/enterprise-risk';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

const Tab: FC = () => {
  useI18NSummaryHelpContent('enterpriseRisks.help');
  const id = useGetGuidParam('id');
  const navigate = useNavigate();
  const objectToolbarEnabled = useIsFeatureFlagEnabled('object_toolbar');
  const { data, loading: loadingEnterpriseRisk } = useGetEnterpriseRiskById({
    queryArgs: { id },
  });

  const enterpriseRisk = data?.enterprise_risk[0];

  const {
    hasPermission: canEditEnterpriseRisk,
    loading: canEditEnterpriseRiskLoading,
  } = useHasPermissionQuery('update:enterprise_risk');

  const { updateEnterpriseRisk } = useUpdateEnterpriseRisk();

  const onSave = async (riskFormData: RiskFormDataFields) => {
    // Guard the ROW, not the route param: the token below must come from a
    // loaded snapshot or the update runs unlocked (`optimisticLockWhere`
    // drops the predicate for a missing token). Matches the impact and
    // assessment siblings (COI-679).
    if (!enterpriseRisk) {
      throw new Error('Missing enterprise risk');
    }
    await updateEnterpriseRisk({
      Id: id,
      // Optimistic-concurrency token: the snapshot this form was loaded from.
      // A second editor saving over a newer row is rejected with a 409 rather
      // than silently winning (COI-622).
      OriginalTimestamp: enterpriseRisk.ModifiedAtTimestamp,
      Tier: riskFormData.Tier,
      Title: riskFormData.Title,
      Description: riskFormData.Description,
      Treatment: riskFormData.Treatment,
      CustomAttributeData: riskFormData.CustomAttributeData ?? null,
      ParentId: riskFormData.ParentRiskId ?? null,
    });
  };
  const onDismiss = () => navigate(-1);

  return (
    <>
      <RiskForm
        enterpriseRisk
        publishToolbarState={objectToolbarEnabled}
        onSave={onSave}
        onDismiss={onDismiss}
        values={{
          ...defaultValues,
          ...enterpriseRisk,
          Description: enterpriseRisk?.Description ?? '',
          Tier: enterpriseRisk?.Tier as 1 | 2 | 3,
          ParentRiskId: enterpriseRisk?.ParentId ?? null,
        }}
        readOnly={
          !canEditEnterpriseRisk ||
          canEditEnterpriseRiskLoading ||
          loadingEnterpriseRisk
        }
        riskId={id}
      />
    </>
  );
};

export default Tab;
